import React, { createContext, useContext, useMemo, useState } from 'react'
import { isProviderACompany, isProviderAPos } from '../../../services/Provider/Provider.service'
import { can as canRoleService } from '../../../services/Role/role.service'
import { FeatureContext } from '../FeatureProvider/FeatureContext'

const UserContext = createContext({})

// Lightweight context that only holds user state, lives above FeatureProvider
const UserStateContext = createContext({})
const UserStateProvider = ({children, baseUser = {}}) => {
    const [user, setUser] = useState(baseUser)
    return (
      <UserStateContext.Provider value={{user, setUser}}>
          {children}
      </UserStateContext.Provider>
    )
}

const UserProvider = ({children}) => {
    const {user, setUser} = useContext(UserStateContext)
    const {isEnabled} = useContext(FeatureContext)

    user.getFirstPosFromLoginContext = () => {
        if (isProviderAPos(user.provider)) {
            return user.provider.object.id
        }

        const providers = user.loginContext.providers

        if (isProviderACompany(user.provider)) {
            const [first] = providers
              .filter(p => isProviderAPos(p) && p.object.company.id === user.provider.object.id)
              .sort((a, b) => a.object.name.localeCompare(b.object.name))

            return first?.object.id
        }

        if (user.loginContext?.type === 'OWN_SCOPE') {
            const [first] = providers
              .filter(isProviderAPos)
              .sort((a, b) => a.object.name.localeCompare(b.object.name))

            return first?.object.id
        }

        return null
    }

    user.getUserCompany = () => {
        return user.provider ? (isProviderACompany(user.provider) ? user.provider.object : user.provider.object.company) : null
    }

    user.getUserPos = () => {
        return isProviderAPos(user.provider) ? user.provider.object : null
    }

    user.getCompanyCurrency = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'EUR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.currencyCode :
          user.provider.object.company.country.currencyCode
    }

    user.getCompanyLocale = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'fr-FR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.locale :
          user.provider.object.company.country.locale
    }

    user.getCompanyCountry = () => {
        if (user.loginContext.type === 'OWN_SCOPE') {
            return 'FR'
        }

        return user.provider.type === 'COMPANY' ?
          user.provider.object.country.code :
          user.provider.object.company.country.code
    }

    user.isAdmin = () => {
        return user.provider?.role.name === 'POWER_USER'
    }

    user.isLoggedInOwnScope = user.loginContext?.type === 'OWN_SCOPE'

    const canCreateCategories = () => {
        if (user.provider.type === 'COMPANY') {
            return true
        }

        const [provider] = user.providers.filter(p => p.id === user.provider.id)
        return provider?.object?.canCreateCategories
    }

    const canExtendCategories = () => {
        if (user.provider.type === 'COMPANY') {
            return true
        }

        return user.provider?.object?.canExtendCategories
    }

    const can = (action, resource) => canRoleService(user, action, resource)

    const activeVariant = useMemo(() => {
        return user?.variant ?? undefined
    }, [user])

    const isLoggedIn = useMemo(() => {
        return !!user && !!user.id
    }, [user])

    const landingRoute = (roleName = null, dashboardSoftRolloutTarget = localStorage.getItem('dashboard_soft_rollout_target')) => {
        const dashboardSoftRolloutTargetAnalytics = dashboardSoftRolloutTarget === 'analytics'
        const canAccessDashboard =
          isEnabled('dashboard') &&
          !dashboardSoftRolloutTargetAnalytics

        switch (roleName || user.provider?.role.name) {
            case 'ORDER_PREPARER':
                return '/orders/current'
            default:
                return canAccessDashboard ? '/dashboard' : '/analytics'
        }
    }

    return (
      <UserContext.Provider
        value={{user, isLoggedIn, setUser, can, landingRoute, canCreateCategories, canExtendCategories, activeVariant}}>
          {children}
      </UserContext.Provider>
    )
}

export { UserProvider, UserStateProvider, UserStateContext, UserContext }
